import { Card, Flex, Form, Input, Row, Space, Tag, Upload } from 'antd'
import { FormInstance } from 'antd/lib'
import dayjs from 'dayjs'

import {
  InternalInventoryDocumentResponse2,
  InternalInventoryDocumentState,
  InventoryDocumentState
} from 'src/api/requests'
import { CustomItemRender, Text } from 'src/components'
import { useStyles } from 'src/features/stock/components/inputs/styles'
import { getStatus } from 'src/features/stock/utils'
import { FileDocument, onDownloadFile } from 'src/hooks'
import { dayMonthYearHourMinutesFormatSeparator } from 'src/libs'
import { useLocales } from 'src/locales'

import { LocalInputsCommentSection } from './LocalInputsCommentSection'
import { LocalInputsConsumablesTable } from './LocalInputsConsumablesTable'

type Props = {
  form: FormInstance
  item?: InternalInventoryDocumentResponse2
}

export const LocalInputConfirmedDetails = ({ form, item }: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles({ hasId: true })

  const isDeliveryDateDisabled =
    (item &&
      (item?.state === InternalInventoryDocumentState.ACCEPTED ||
        item?.state === InternalInventoryDocumentState.REJECTED)) ||
    !item

  return (
    <Flex vertical gap={24}>
      <Card>
        <Flex vertical gap={24}>
          <Flex vertical gap={16}>
            <Flex vertical gap={8}>
              <Flex justify="space-between">
                <Space>
                  <Text weight={400}>
                    {t('stock_applications.issuance_based_on_inquire')}:
                  </Text>
                  <Text strong>#{item?.code}</Text>
                </Space>
                <Tag
                  color={
                    getStatus(t, item?.state ?? InventoryDocumentState.NEW)
                      .color
                  }
                >
                  {getStatus(t, item?.state ?? InventoryDocumentState.NEW).text}
                </Tag>
              </Flex>

              <Space>
                <Text weight={400}>
                  {t('stock_applications.request_date')}:
                </Text>
                <Text strong>
                  {dayjs(item?.initializedOn).format(
                    dayMonthYearHourMinutesFormatSeparator
                  )}
                </Text>
              </Space>

              <Space>
                <Text weight={400}>
                  {t('stock_applications.initiated_by')}:
                </Text>
                <Text strong>
                  {item?.initializedBy?.firstName}{' '}
                  {item?.initializedBy?.lastName} -
                </Text>
                <Text strong>{item?.role?.name}</Text>
              </Space>
            </Flex>
          </Flex>
        </Flex>
      </Card>

      <Card>
        <Card title={t('stock.stocks')}>
          <Form.List name="medicine">
            {(fields, { add, remove }) => (
              <LocalInputsConsumablesTable
                item={item}
                fields={fields}
                add={add}
                remove={remove}
                form={form}
              />
            )}
          </Form.List>
        </Card>
      </Card>

      <Card>
        <Flex vertical gap={24}>
          <Card
            title={t('stock_applications.uploaded_documents')}
            className={styles.extraCardStyles}
          >
            <Upload
              itemRender={(_, file: FileDocument) => (
                <CustomItemRender
                  file={file}
                  onDownload={() => onDownloadFile(t, file?.id, file?.name)}
                />
              )}
              defaultFileList={item?.mediaFiles?.map((file, index) => ({
                ...file,
                uid: String(index),
                name: file?.name ?? `file-${index}`
              }))}
              disabled
            />
          </Card>

          <LocalInputsCommentSection data={item} />

          {!isDeliveryDateDisabled && (
            <Row>
              <Form.Item
                name="commentNew"
                className="full-width"
                label={t('analysis.cancel_test_comments')}
              >
                <Input.TextArea
                  rows={5}
                  placeholder={t('common.leave_comment')}
                  disabled={isDeliveryDateDisabled}
                />
              </Form.Item>
            </Row>
          )}
        </Flex>
      </Card>
    </Flex>
  )
}
